/** @format */
import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";
import { HttpService } from "./http.service";
import { AlertService } from "./alert.service";
import { environment } from "../../../environments/environment";

export interface User {
  id: number;
  email: string;
  name: string;
  username: string;
  mobile: string;
  socialType: string;
  lastLoginAt: string;
  userStatus: string;
  isPushAgree: boolean;
  isMarketingAgree: boolean;
  monthLocation: string;
  monthLocationDetail: string;
  adminMemo: string;
  birthDate: string;
  gender: string;
  department: string;
  position: string;
  authorities: Array<{ authority: string }>;
  enabled: boolean;
  accountNonExpired: boolean;
  accountNonLocked: boolean;
  credentialsNonExpired: boolean;
  createdAt: string;
  updatedAt: string;
  deletedAt: string;
  firebaseToken: string;
  adId: string;
  deviceId: string;
  uid: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  email: string;
  password: string;
  name: string;
}

export interface AuthResponse {
  token: string;
  users: User;
  requiredResetPassword: boolean;
}

@Injectable({
  providedIn: "root",
})
export class AuthService {
  private currentUserSubject = new BehaviorSubject<User | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();

  constructor(
    private httpService: HttpService,
    private alertService: AlertService
  ) {
    this.loadUserFromStorage();
  }

  // 로컬 스토리지에서 사용자 정보 로드
  private loadUserFromStorage(): void {
    const token = localStorage.getItem(environment.auth.tokenKey);
    const userStr = localStorage.getItem("currentUser");

    if (token && userStr) {
      try {
        const user = JSON.parse(userStr);
        this.currentUserSubject.next(user);
      } catch (error) {
        this.clearAuthData();
      }
    }
  }

  // 인증 데이터 저장
  private saveAuthData(response: AuthResponse): void {
    // 토큰 만료 시간을 24시간으로 설정 (서버에서 제공하지 않는 경우)
    const expiryTime = Date.now() + 24 * 60 * 60 * 1000; // 24시간

    localStorage.setItem(environment.auth.tokenKey, response.token);
    localStorage.setItem(
      environment.auth.tokenExpiryKey,
      expiryTime.toString()
    );
    localStorage.setItem("currentUser", JSON.stringify(response.users));

    this.currentUserSubject.next(response.users);
  }

  // 인증 데이터 삭제
  private clearAuthData(): void {
    localStorage.removeItem(environment.auth.tokenKey);
    localStorage.removeItem(environment.auth.tokenExpiryKey);
    localStorage.removeItem("currentUser");

    this.currentUserSubject.next(null);
  }

  // 토큰 만료 확인
  private isTokenExpired(): boolean {
    const expiryTime = localStorage.getItem(environment.auth.tokenExpiryKey);
    if (!expiryTime) return true;

    return Date.now() > parseInt(expiryTime);
  }

  // 회원가입
  async registerUser(request: RegisterRequest): Promise<AuthResponse> {
    try {
      const response = await this.httpService.doPost(
        "/api/auth/signup",
        request
      );
      this.saveAuthData(response.result);
      return response.result;
    } catch (error) {
      this.showErrorMessage(error);
      throw error;
    }
  }

  // 로그인
  async loginUser(request: LoginRequest): Promise<AuthResponse> {
    try {
      const response = await this.httpService.doPost(
        "/api/users/emailLogin",
        request
      );
      console.log("response.", response);

      // response.result가 있는 경우와 없는 경우를 모두 처리
      const authData = response.result || response;
      this.saveAuthData(authData);
      return authData;
    } catch (error) {
      this.showErrorMessage(error);
      throw error;
    }
  }

  // 로그아웃
  async logoutUser(): Promise<void> {
    try {
      await this.httpService.doPost("/api/auth/logout", {}, true);
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      this.clearAuthData();
    }
  }

  // 현재 사용자 정보 가져오기
  async getCurrentUser(): Promise<User | null> {
    // 토큰이 만료되었으면 로그인 페이지로 리다이렉트
    if (this.isTokenExpired()) {
      this.clearAuthData();
      return null;
    }

    try {
      // 로컬 스토리지에서 사용자 정보를 가져옴
      const userStr = localStorage.getItem("currentUser");
      if (userStr) {
        const user = JSON.parse(userStr);
        this.currentUserSubject.next(user);
        return user;
      }
      return null;
    } catch (error) {
      this.clearAuthData();
      return null;
    }
  }

  // 토큰 갱신 (새로운 API 구조에서는 refresh token이 없으므로 재로그인 필요)
  private async refreshToken(): Promise<void> {
    // 새로운 API 구조에서는 refresh token이 없으므로
    // 토큰이 만료되면 재로그인이 필요합니다.
    this.clearAuthData();
    throw new Error("토큰이 만료되었습니다. 다시 로그인해주세요.");
  }

  // 로그인 상태 확인
  isLoggedIn(): boolean {
    const token = localStorage.getItem(environment.auth.tokenKey);
    const isExpired = this.isTokenExpired();
    console.log("AuthService.isLoggedIn - 토큰:", !!token, "만료:", isExpired);
    return !!token && !isExpired;
  }

  // 토큰 정보 디버깅용
  debugTokenInfo(): void {
    const token = localStorage.getItem(environment.auth.tokenKey);
    const expiryTime = localStorage.getItem(environment.auth.tokenExpiryKey);
    const userStr = localStorage.getItem("currentUser");

    console.log("=== 토큰 디버깅 정보 ===");
    console.log("토큰 존재:", !!token);
    console.log("토큰 값:", token ? token.substring(0, 20) + "..." : "없음");
    console.log("만료 시간:", expiryTime);
    console.log("사용자 정보:", userStr ? "있음" : "없음");
    console.log("현재 시간:", new Date().toISOString());
    console.log("토큰 만료 여부:", this.isTokenExpired());
    console.log("========================");
  }

  // 비밀번호 변경
  async changePassword(
    currentPassword: string,
    newPassword: string
  ): Promise<void> {
    try {
      await this.httpService.doPost(
        "/api/auth/change-password",
        {
          currentPassword,
          newPassword,
        },
        true
      );
      this.alertService.successmsg(
        "비밀번호가 성공적으로 변경되었습니다.",
        "비밀번호 변경"
      );
    } catch (error) {
      this.showErrorMessage(error);
      throw error;
    }
  }

  // 비밀번호 재설정 이메일 발송
  async sendPasswordReset(email: string): Promise<void> {
    try {
      await this.httpService.doPost("/api/auth/forgot-password", { email });
      this.alertService.successmsg(
        "비밀번호 재설정 이메일이 발송되었습니다.",
        "이메일 발송"
      );
    } catch (error) {
      this.showErrorMessage(error);
      throw error;
    }
  }

  // 에러 메시지 처리
  private showErrorMessage(error: any): void {
    let message = "알 수 없는 오류가 발생했습니다.";
    let title = "오류";

    if (error && typeof error === "string") {
      message = error;
    } else if (error && error.message) {
      message = error.message;
    }

    // HTTP 상태 코드별 메시지
    if (error && error.status) {
      switch (error.status) {
        case 400:
          title = "잘못된 요청";
          break;
        case 401:
          title = "인증 실패";
          message = "이메일 또는 비밀번호가 올바르지 않습니다.";
          break;
        case 403:
          title = "접근 거부";
          message = "접근 권한이 없습니다.";
          break;
        case 404:
          title = "찾을 수 없음";
          message = "요청한 리소스를 찾을 수 없습니다.";
          break;
        case 500:
          title = "서버 오류";
          message = "서버에서 오류가 발생했습니다. 잠시 후 다시 시도해주세요.";
          break;
      }
    }

    this.alertService.errormsg(message, title);
  }
}
